/**
 * Validation Rules — Reusable regex validation for frontend.
 * Mirrors the Python validation_rules.py for consistent behavior.
 */

const PAN_REGEX = /^([A-Za-z]{3}[HPhp])[A-Za-z]\d{4}[A-Za-z]$/;
const FOLIO_REGEX = /^[A-Z0-9]{1,20}$/i;
const ARN_CODE_REGEX = /^(ARN[-:]?[A-Z0-9]+|INVESTOR|\d{1,6})$/i;
const BANK_ACCOUNT_REGEX = /^[0-9]{9,18}$/;
const IFSC_CODE_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/i;
const MICR_CODE_REGEX = /^[0-9]{9}$/;
const MOBILE_BASIC = /^[6-9][0-9]{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AADHAR_REGEX = /^[0-9]{12}$/;
const NAME_REGEX = /^[a-zA-Z\s\-.']{2,100}$/;
const UMRN_REGEX = /^[A-Z0-9]{1,20}$/i;
const AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;
const CHEQUE_REGEX = /^[0-9]{6,10}$/;
const SCHEME_CODE_REGEX = /^[A-Z0-9]{6}$/i;
const EUIN_REGEX = /^E\d{5,7}$/i;
const PINCODE_REGEX = /^[1-9]\d{5}$/;
const DATE_REGEX = /^\d{2}[/-]\d{2}[/-](\d{4}|\d{2})$/;

const INVALID_MOBILES = [
  "0000000000", "1111111111", "2222222222", "3333333333",
  "4444444444", "5555555555", "6666666666", "7777777777",
  "8888888888", "9999999999", "1234567890"
];

const validatePan = (v) => PAN_REGEX.test(v);
const validateFolio = (v) => FOLIO_REGEX.test(v);
const validateArn = (v) => ARN_CODE_REGEX.test(v.toUpperCase());
const validateBankAccount = (v) => BANK_ACCOUNT_REGEX.test(v);
const validateIfsc = (v) => IFSC_CODE_REGEX.test(v);
const validateMicr = (v) => MICR_CODE_REGEX.test(v);
const validateMobile = (v) => {
  const digits = v.replace(/\D/g, '').slice(-10);
  if (INVALID_MOBILES.includes(digits)) return false;
  return MOBILE_BASIC.test(digits);
};
const validateEmail = (v) => EMAIL_REGEX.test(v);
const validateAadhar = (v) => AADHAR_REGEX.test(v);
const validateName = (v) => v.length >= 2 && v.length <= 100 && NAME_REGEX.test(v);
const validateAmount = (v) => {
  const cleaned = String(v).trim().replace(/%$/, '');
  const num = parseFloat(cleaned);
  return !isNaN(num) && num >= 0 && AMOUNT_REGEX.test(String(num));
};
const validateCheque = (v) => CHEQUE_REGEX.test(v);
const validateScheme = (v) => SCHEME_CODE_REGEX.test(v);
const validateUmrn = (v) => UMRN_REGEX.test(v);
const validateEuin = (v) => EUIN_REGEX.test(v);
const validatePincode = (v) => PINCODE_REGEX.test(v);
const validateDate = (v) => DATE_REGEX.test(v);

// Field name → validator mapping
const FIELD_VALIDATORS = {
  pan_pekrn: validatePan, pan: validatePan, pan_number: validatePan,
  first_name: validateName, last_name: validateName, middle_name: validateName,
  name: validateName, nominee_name: validateName, guardian_name: validateName,
  "first unit holder": validateName, "first applicant's name": validateName,
  mobile_no: validateMobile, mobile_number: validateMobile, mobile: validateMobile,
  email_id: validateEmail, email: validateEmail,
  account_no: validateBankAccount, account_number: validateBankAccount,
  bank_account_number: validateBankAccount, "investor bank account no": validateBankAccount,
  ifsc_code: validateIfsc, ifsc: validateIfsc,
  micr_code: validateMicr, micr: validateMicr,
  folio_no: validateFolio, folio_number: validateFolio, "folio no": validateFolio,
  "existing folio no": validateFolio, existing_folio_number: validateFolio,
  arn_ria_pmrn_code: validateArn, arn_code: validateArn,
  broker_code_arn_code: validateArn, internal_code_for_sub_agent_employee: validateArn,
  euin: validateEuin, umrn: validateUmrn,
  aadhar: validateAadhar, aadhar_number: validateAadhar,
  amount: validateAmount, sip_amount: validateAmount, "sip amount": validateAmount,
  "each sip amount": validateAmount, "stp amount": validateAmount, "swp amount": validateAmount,
  amount_inr: validateAmount, amount_in_figures: validateAmount,
  net_amount_paid: validateAmount, share_percentage: validateAmount,
  cheque_number: validateCheque, cheque_no: validateCheque,
  scheme_code: validateScheme,
  pincode: validatePincode, pin_code: validatePincode,
  date_of_birth: validateDate, dob: validateDate,
  sip_start_date: validateDate, sip_end_date: validateDate,
  "sip start date": validateDate, "sip end date": validateDate,
  cheque_date: validateDate, date: validateDate,
  mandate_start_date: validateDate, mandate_end_date: validateDate,
};

/**
 * Validate a field value by its name.
 * @param {string} fieldName - The leaf field name (e.g., "pan_pekrn", "mobile_no")
 * @param {string} value - The value to validate
 * @returns {null|boolean} null if no validator exists, true if valid, false if invalid
 */
export const validateField = (fieldName, value) => {
  if (!value || value === 'YES' || value === 'true' || value === 'True') return null;
  const validator = FIELD_VALIDATORS[fieldName.toLowerCase()];
  if (!validator) return null;
  return validator(String(value));
};

export default validateField;
